import React, { useState } from 'react';
import { DetailedAnalysisEmailParticipant } from '../../../../../../../store/deals/deals.types';
import { convertValueIntoTime } from '../../../../utils/utils';
import { DetailedViewTooltip } from '../../../tooltip/DetailedViewTooltip';
import { sandGlass } from './assets';

type Props = {
  participants?: {
    leftParticipant: DetailedAnalysisEmailParticipant;
    rightParticipant: DetailedAnalysisEmailParticipant;
  };
  replyTime?: {
    currentEmailValue: number | null;
    insightText: string | null;
    previousEmailValue: number | null;
    units: 'hrs';
  };
};

export const ReplyTime: React.FC<Props> = ({
  replyTime,
  participants,
}): JSX.Element => {
  const [leftTooltipOpen, setLeftTooltipOpen] = useState(false);
  const [rightTooltipOpen, setRightTooltipOpen] = useState(false);

  const toggleLeftTooltip = () => {
    setLeftTooltipOpen(!leftTooltipOpen);
  };

  const toggleRightTooltip = () => {
    setRightTooltipOpen(!rightTooltipOpen);
  };

  const defineLeftValue = () => {
    let leftValue = 'N/A';
    let leftValueForCHeck: number | null = null;
    if (
      participants?.leftParticipant?.isSender &&
      replyTime?.currentEmailValue
    ) {
      leftValue = convertValueIntoTime(replyTime.currentEmailValue);
      leftValueForCHeck = replyTime.currentEmailValue;
    } else if (
      !participants?.leftParticipant?.isSender &&
      replyTime?.previousEmailValue
    ) {
      leftValue = convertValueIntoTime(replyTime.previousEmailValue);
      leftValueForCHeck = replyTime.previousEmailValue;
    }

    return { leftValue, leftValueForCHeck };
  };

  const { leftValue, leftValueForCHeck } = defineLeftValue();

  const defineRightValue = () => {
    let rightValue = 'N/A';
    let rightValueForCHeck: number | null = null;
    if (
      participants?.rightParticipant?.isSender &&
      replyTime?.currentEmailValue
    ) {
      rightValue = convertValueIntoTime(replyTime.currentEmailValue);
      rightValueForCHeck = replyTime.currentEmailValue;
    } else if (
      !participants?.rightParticipant?.isSender &&
      replyTime?.previousEmailValue
    ) {
      rightValue = convertValueIntoTime(replyTime.previousEmailValue);
      rightValueForCHeck = replyTime.previousEmailValue;
    }

    return { rightValue, rightValueForCHeck };
  };

  const { rightValue, rightValueForCHeck } = defineRightValue();

  const defineSvgIconsClassName = () => {
    let leftIconClassName = 'reply-time__body__left-person__image-section-svg';

    let rightIconClassName =
      'reply-time__body__right-person__image-section-svg';

    if (
      leftValueForCHeck &&
      rightValueForCHeck &&
      leftValueForCHeck !== rightValueForCHeck
    ) {
      if (leftValueForCHeck > rightValueForCHeck) {
        leftIconClassName =
          'active reply-time__body__left-person__image-section-svg';
      } else if (leftValueForCHeck < rightValueForCHeck) {
        rightIconClassName =
          'active reply-time__body__right-person__image-section-svg';
      }
    }

    return { leftIconClassName, rightIconClassName };
  };

  const { leftIconClassName, rightIconClassName } = defineSvgIconsClassName();

  const attrs = {
    wrapper: { className: 'reply-time' },
    header: {
      wrapper: { className: 'reply-time__header' },
      bold: { className: 'reply-time__header-bold' },
      tooltip: {
        text: `Comparison between your reply time and your
      prospects' reply time in the previous two messages`,
        width: '259px',
        height: '42px',
        top: '100%',
        iconMarginLeft: '3px',
      },
    },
    body: {
      wrapper: { className: 'reply-time__body' },
      leftPerson: {
        wrapper: { className: 'reply-time__body__left-person' },
        name: { className: 'reply-time__body__left-person-name' },
        imageSection: {
          wrapper: {
            className: 'reply-time__body__left-person__image-section',
          },
          sandGlass: {
            src: sandGlass,
            className: leftIconClassName,
            onMouseEnter: toggleLeftTooltip,
            onMouseLeave: toggleLeftTooltip,
          },
          tooltip: {
            className: leftTooltipOpen
              ? 'open reply-time__body__left-person__image-section-tooltip'
              : 'reply-time__body__left-person__image-section-tooltip',
          },
          capital: {
            className:
              'reply-time__body__left-person__image-section-tooltip-capital',
          },
        },
        time: { className: 'reply-time__body__left-person-time' },
        timeBold: { className: 'reply-time__body__left-person-time-bold' },
        timeNA: {
          className: 'reply-time__body__left-person-time-n-a',
        },
      },
      bothNa: { className: 'reply-time__body-na' },
      divider: {
        className: 'reply-time__body-divider',
      },
      rightPerson: {
        wrapper: { className: 'reply-time__body__right-person' },
        name: { className: 'reply-time__body__right-person-name' },
        imageSection: {
          wrapper: {
            className: 'reply-time__body__right-person__image-section',
          },
          sandGlass: {
            src: sandGlass,
            className: rightIconClassName,
            onMouseEnter: toggleRightTooltip,
            onMouseLeave: toggleRightTooltip,
          },
          tooltip: {
            className: rightTooltipOpen
              ? 'open reply-time__body__right-person__image-section-tooltip'
              : 'reply-time__body__right-person__image-section-tooltip',
          },
          capital: {
            className:
              'reply-time__body__right-person__image-section-tooltip-capital',
          },
        },
        time: { className: 'reply-time__body__right-person-time' },
        timeBold: { className: 'reply-time__body__right-person-time-bold' },
        timeNA: {
          className: 'reply-time__body__right-person-time-n-a',
        },
      },
    },
    footer: { className: 'reply-time__footer' },
  };

  const defineUserName = (participant?: DetailedAnalysisEmailParticipant) => {
    let name = `${participant?.metadata?.name?.split(' ')[0]}'s` || '';
    if (participant && participant?.isUser) {
      name = 'Your';
    }
    return name;
  };

  const isBothNaCheck = leftValue === 'N/A' && rightValue === 'N/A';

  const leftValueToShow =
    leftValue.length && !isBothNaCheck ? (
      <span {...attrs.body.leftPerson.time}>
        {leftValue.includes(' ') ? (
          <>
            <span {...attrs.body.leftPerson.timeBold}>
              {leftValue.split(' ').slice(0, -1)}
            </span>
            {leftValue.split(' ').pop()}
          </>
        ) : (
          <span {...attrs.body.leftPerson.timeNA}>N/A</span>
        )}
      </span>
    ) : (
      <span {...attrs.body.leftPerson.time} />
    );

  const rightValueToShow =
    rightValue.length && !isBothNaCheck ? (
      <span {...attrs.body.rightPerson.time}>
        {rightValue.includes(' ') ? (
          <>
            <span {...attrs.body.rightPerson.timeBold}>
              {rightValue.split(' ').slice(0, -1)}
            </span>
            {rightValue.split(' ').pop()}
          </>
        ) : (
          <span {...attrs.body.rightPerson.timeNA}>N/A</span>
        )}
      </span>
    ) : (
      <span {...attrs.body.rightPerson.time} />
    );

  const leftName = defineUserName(participants?.leftParticipant);

  const rightName = defineUserName(participants?.rightParticipant);

  const leftTooltip = (
    <span {...attrs.body.leftPerson.imageSection.tooltip}>
      <span {...attrs.body.leftPerson.imageSection.capital}>{leftName}</span>
      reply time to {rightName === 'Your' ? 'your' : rightName}{' '}
      {participants?.leftParticipant?.isSender ? '' : 'previous'} message
    </span>
  );

  const rightTooltip = (
    <span {...attrs.body.rightPerson.imageSection.tooltip}>
      <span {...attrs.body.rightPerson.imageSection.capital}>{rightName}</span>{' '}
      reply time to {leftName === 'Your' ? 'your' : leftName}{' '}
      {participants?.rightParticipant?.isSender ? '' : 'previous'} message
    </span>
  );

  const defineFooterMessage = () => {
    let message = '';
    if (replyTime?.insightText) {
      message = replyTime.insightText;
    } else if (leftValue === 'N/A' && rightValue !== 'N/A') {
      if (leftName === 'Your') {
        message = `You haven’t replied to ${rightName} last message`;
      } else {
        const nameWithoutAposS =
          leftName.length && leftName.includes(`'s`)
            ? leftName.split(`'`)[0]
            : leftName;
        message = `${nameWithoutAposS} hasn’t replied to ${
          rightName === 'Your' ? 'your' : rightName
        } last message`;
      }
    } else if (rightValue === 'N/A' && leftValue !== 'N/A') {
      if (rightName === 'Your') {
        message = `You haven’t replied to ${leftName} last message`;
      } else {
        const nameWithoutApos =
          rightName.length && rightName.includes(`'s`)
            ? rightName.split(`'`)[0]
            : rightName;
        message = `${nameWithoutApos} hasn’t replied to ${
          leftName === 'Your' ? 'your' : leftName
        } last message`;
      }
    } else {
      message = 'Not enough data available';
    }
    return message;
  };

  const footerMessage = defineFooterMessage();

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.header.wrapper}>
        <span {...attrs.header.bold}>Reply Time</span>
        (current message vs. previous)
        <DetailedViewTooltip {...attrs.header.tooltip} />
      </div>
      <div {...attrs.body.wrapper}>
        <div {...attrs.body.leftPerson.wrapper}>
          <span {...attrs.body.leftPerson.name}>
            {leftName === 'Your' ? 'Me' : leftName.split(`'`)[0]}
          </span>
          <div {...attrs.body.leftPerson.imageSection.wrapper}>
            {leftTooltip}
            <img {...attrs.body.leftPerson.imageSection.sandGlass} alt="" />
          </div>
          {leftValueToShow}
        </div>
        <div {...attrs.body.divider} />
        {isBothNaCheck ? <div {...attrs.body.bothNa}>N/A</div> : null}
        <div {...attrs.body.rightPerson.wrapper}>
          <span {...attrs.body.rightPerson.name}>
            {rightName === 'Your' ? 'Me' : rightName.split(`'`)[0]}
          </span>
          <div {...attrs.body.rightPerson.imageSection.wrapper}>
            {rightTooltip}
            <img {...attrs.body.rightPerson.imageSection.sandGlass} alt="" />
          </div>
          {rightValueToShow}
        </div>
      </div>
      <div {...attrs.footer}>{footerMessage}</div>
    </div>
  );
};
